/**
 * Pure Concierge payload parsing and proposal guardrails.
 *
 * The web component owns rendering and transport; this module keeps the
 * untrusted payload normalization independently testable and reusable.
 */
export type QuoteAppointment = {
  serviceId?: string;
  serviceName?: string;
  providerId?: string;
  providerName?: string;
  locationId?: string;
  locationName?: string;
  startAt?: string;
  endAt?: string;
  timezone?: string;
  slotKey?: string;
  durationMinutes?: number;
  persons?: number;
};
export type QuoteRule = { label?: string; reason?: string };
export type QuoteOccurrence = QuoteAppointment & {
  sequence: number;
  quoteId?: string;
  holdId?: string;
  requestedStartAt?: string;
  requestedEndAt?: string;
  amountCents?: number;
  totalCents?: number;
  taxCents?: number;
  currency?: string;
  status?: string;
  adjusted?: boolean;
  adjustmentReason?: string;
  adjustmentAccepted?: boolean;
  lines?: unknown[];
  appliedRules?: QuoteRule[];
};
export type QuoteSnapshot = {
  id: string;
  proposalId?: string;
  kind?: string;
  targetKind?: string;
  currency?: string;
  totalCents: number;
  expiresAt: string;
  lines?: unknown[];
  appointment?: QuoteAppointment;
  occurrences?: QuoteOccurrence[];
  appliedRules?: QuoteRule[];
  dynamicAdjustmentCents?: number;
  adjusted?: boolean;
  adjustmentReason?: string;
  adjustmentAccepted?: boolean;
  consentRequired?: boolean;
};
export type CheckoutSnapshot = { paymentId?: string; bookingId?: string; eventOrderId?: string; checkoutUrl?: string; status?: string; statusDetail?: string; bookingStatus?: string; eventOrderStatus?: string; recurringSeriesStatus?: string; recurringPaymentState?: string; amountCents?: number; currency?: string; statusToken?: string };
export type ConciergeCustomer = { firstName: string; email: string; lastName?: string; phone?: string; consent?: boolean };
export type ConciergePaymentMethod = "mercadopago" | "stripe" | "paypal" | "on_site";
export type ConciergeAction =
  | { type: "confirm-proposal"; proposalId: string; customer: ConciergeCustomer; adjustmentAccepted?: boolean; paymentMethod?: ConciergePaymentMethod }
  | { type: "update-proposal"; proposalId: string; occurrences: Array<{ sequence: number; include?: boolean; quoteId?: string; holdId?: string; slotKey?: string; startAt?: string; endAt?: string; providerId?: string | null; locationId?: string | null; adjustmentAccepted?: boolean }> };

export function randomId(prefix: string): string {
  try { return `${prefix}-${crypto.randomUUID()}`; }
  catch { return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

export function money(cents: number, currency = "EUR", locale = "es-ES"): string {
  try { return new Intl.NumberFormat(locale, { style: "currency", currency }).format(cents / 100); }
  catch { return `${(cents / 100).toFixed(2)} ${currency}`; }
}

export function textFromParts(parts: unknown): string {
  if (!Array.isArray(parts)) return "";
  return parts.filter((part): part is { type: "text"; text: string } => Boolean(part) && typeof part === "object" && (part as Record<string, unknown>).type === "text" && typeof (part as Record<string, unknown>).text === "string")
    .map((part) => part.text).join("");
}

export function objectRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

export function firstString(records: Array<Record<string, unknown> | undefined>, ...keys: string[]): string | undefined {
  for (const record of records) {
    for (const key of keys) {
      if (typeof record?.[key] === "string" && record[key].trim()) return record[key].trim();
    }
  }
  return undefined;
}

export function firstNumber(records: Array<Record<string, unknown> | undefined>, ...keys: string[]): number | undefined {
  for (const record of records) {
    for (const key of keys) {
      if (typeof record?.[key] === "number" && Number.isFinite(record[key])) return Math.max(0, Math.round(record[key]));
    }
  }
  return undefined;
}

function ruleSnapshots(value: unknown): QuoteRule[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rules = value.flatMap((item) => {
    if (typeof item === "string" && item.trim()) return [{ label: item.trim() }];
    const record = objectRecord(item);
    if (!record) return [];
    const label = firstString([record], "label", "name");
    const reason = firstString([record], "reason", "description");
    return label || reason ? [{ ...(label ? { label } : {}), ...(reason ? { reason } : {}) }] : [];
  });
  return rules.length ? rules : undefined;
}

function proposalOccurrenceStatus(value: string | undefined): string | undefined {
  if (value === "draft") return "proposed";
  if (value === "cancelled") return "excluded";
  return value;
}

export function formatAppointmentRange(appointment: QuoteAppointment, locale: string): string | undefined {
  if (!appointment.startAt) return undefined;
  const start = new Date(appointment.startAt);
  if (Number.isNaN(start.getTime())) return undefined;
  const dateOptions: Intl.DateTimeFormatOptions = { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  const timeOptions: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  const makeFormatter = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
    try { return new Intl.DateTimeFormat(locale, { ...options, ...(appointment.timezone ? { timeZone: appointment.timezone } : {}) }); }
    catch { return new Intl.DateTimeFormat(locale, options); }
  };
  const startText = makeFormatter(dateOptions).format(start);
  if (!appointment.endAt) return startText;
  const end = new Date(appointment.endAt);
  if (Number.isNaN(end.getTime())) return startText;
  return `${startText} · ${makeFormatter(timeOptions).format(end)}`;
}

export function quoteFromValue(value: unknown, depth = 0): QuoteSnapshot | undefined {
  if (depth > 5 || !value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = quoteFromValue(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const candidate = objectRecord(record.quote) ?? objectRecord(record.proposal) ?? record;
  if (candidate) {
    const item = candidate;
    const id = typeof item.id === "string" ? item.id : typeof item.quoteId === "string" ? item.quoteId : typeof record.quoteId === "string" ? record.quoteId : typeof item.proposalId === "string" ? item.proposalId : typeof record.proposalId === "string" ? record.proposalId : undefined;
    const totalCents = typeof item.totalCents === "number" ? item.totalCents : typeof record.totalCents === "number" ? record.totalCents : undefined;
    const expiresAt = typeof item.expiresAt === "string" ? item.expiresAt : typeof record.expiresAt === "string" ? record.expiresAt : undefined;
    if (id && totalCents !== undefined && expiresAt) {
      const nestedAppointments = [
        item.appointment,
        record.appointment,
        item.slot,
        record.slot,
        item.selectedSlot,
        record.selectedSlot,
        item.details,
        record.details
      ].map(objectRecord);
      const line = (Array.isArray(item.lines) ? item.lines : Array.isArray(record.lines) ? record.lines : [])
        .map(objectRecord)
        .find((entry) => Boolean(entry));
      const sources = [item, ...nestedAppointments, record, line];
      const provider = objectRecord(item.provider) ?? objectRecord(record.provider);
      const location = objectRecord(item.location) ?? objectRecord(record.location);
      const serviceName = firstString(sources, "serviceName", "service_name", "serviceTitle", "serviceLabel") ?? firstString([line], "label", "name");
      const appointment: QuoteAppointment = {
        ...(firstString(sources, "serviceId", "service_id") ? { serviceId: firstString(sources, "serviceId", "service_id") } : {}),
        ...(serviceName ? { serviceName } : {}),
        ...(firstString([provider, ...sources], "providerId", "provider_id") ? { providerId: firstString([provider, ...sources], "providerId", "provider_id") } : {}),
        ...(firstString([provider, ...sources], "providerName", "provider_name", "professionalName", "doctorName", "displayName", "name") ? { providerName: firstString([provider, ...sources], "providerName", "provider_name", "professionalName", "doctorName", "displayName", "name") } : {}),
        ...(firstString([location, ...sources], "locationId", "location_id") ? { locationId: firstString([location, ...sources], "locationId", "location_id") } : {}),
        ...(firstString([location, ...sources], "locationName", "location_name", "venueName", "officeName", "displayName", "name") ? { locationName: firstString([location, ...sources], "locationName", "location_name", "venueName", "officeName", "displayName", "name") } : {}),
        ...(firstString(sources, "startAt", "startsAt", "start", "appointmentStartAt") ? { startAt: firstString(sources, "startAt", "startsAt", "start", "appointmentStartAt") } : {}),
        ...(firstString(sources, "endAt", "endsAt", "end", "appointmentEndAt") ? { endAt: firstString(sources, "endAt", "endsAt", "end", "appointmentEndAt") } : {}),
        ...(firstString(sources, "timezone", "timeZone") ? { timezone: firstString(sources, "timezone", "timeZone") } : {}),
        ...(firstString(sources, "slotKey", "slot_key") ? { slotKey: firstString(sources, "slotKey", "slot_key") } : {}),
        ...(firstNumber(sources, "durationMinutes", "duration_minutes", "duration") !== undefined ? { durationMinutes: firstNumber(sources, "durationMinutes", "duration_minutes", "duration") } : {}),
        ...(firstNumber(sources, "persons", "quantity") !== undefined ? { persons: firstNumber(sources, "persons", "quantity") } : {})
      };
      const quotePricing = objectRecord(item.pricing) ?? objectRecord(record.pricing);
      const appliedRules = ruleSnapshots(item.appliedRules ?? record.appliedRules ?? quotePricing?.appliedRules);
      const lines = Array.isArray(item.lines) ? item.lines : Array.isArray(record.lines) ? record.lines : Array.isArray(quotePricing?.lines) ? quotePricing.lines : undefined;
      const dynamicAdjustmentCents = firstNumber([item, record, quotePricing], "dynamicAdjustmentCents", "dynamic_adjustment_cents");
      const adjusted = item.adjusted === true || record.adjusted === true || (dynamicAdjustmentCents !== undefined && dynamicAdjustmentCents !== 0) || Boolean(appliedRules?.length);
      const adjustmentReason = firstString([item, record], "adjustmentReason", "adjustment_reason") ?? appliedRules?.map((rule) => rule.reason ?? rule.label).find((value): value is string => Boolean(value));
      const rawOccurrences = Array.isArray(item.occurrences) ? item.occurrences : Array.isArray(record.occurrences) ? record.occurrences : undefined;
      const occurrences = rawOccurrences?.flatMap((raw, index) => {
        const row = objectRecord(raw);
        if (!row) return [];
        const occurrencePricing = objectRecord(row.pricing);
        const sources = [row, objectRecord(row.appointment), objectRecord(row.slot), objectRecord(row.provider), objectRecord(row.location)];
        const occurrenceAppliedRules = ruleSnapshots(row.appliedRules ?? occurrencePricing?.appliedRules);
        const occurrenceDynamicAdjustment = firstNumber([row, occurrencePricing], "dynamicAdjustmentCents", "dynamic_adjustment_cents");
        const occurrenceAdjusted = row.adjusted === true || (occurrenceDynamicAdjustment !== undefined && occurrenceDynamicAdjustment !== 0) || Boolean(occurrenceAppliedRules?.length);
        const occurrenceAdjustmentReason = firstString(sources, "adjustmentReason", "adjustment_reason") ?? occurrenceAppliedRules?.map((rule) => rule.reason ?? rule.label).find((value): value is string => Boolean(value));
        const occurrence: QuoteOccurrence = {
          sequence: typeof row.sequence === "number" ? row.sequence : index,
          ...(firstString(sources, "quoteId", "id") ? { quoteId: firstString(sources, "quoteId", "id") } : {}),
          ...(firstString(sources, "holdId") ? { holdId: firstString(sources, "holdId") } : {}),
          ...(firstString(sources, "requestedStartAt", "requested_start_at") ? { requestedStartAt: firstString(sources, "requestedStartAt", "requested_start_at") } : {}),
          ...(firstString(sources, "requestedEndAt", "requested_end_at") ? { requestedEndAt: firstString(sources, "requestedEndAt", "requested_end_at") } : {}),
          ...(firstString(sources, "serviceId", "service_id") ? { serviceId: firstString(sources, "serviceId", "service_id") } : {}),
          ...(firstString(sources, "serviceName", "service_name", "serviceTitle", "serviceLabel") ? { serviceName: firstString(sources, "serviceName", "service_name", "serviceTitle", "serviceLabel") } : {}),
          ...(firstString(sources, "providerId", "provider_id") ? { providerId: firstString(sources, "providerId", "provider_id") } : {}),
          ...(firstString(sources, "providerName", "provider_name", "professionalName", "doctorName", "displayName", "name") ? { providerName: firstString(sources, "providerName", "provider_name", "professionalName", "doctorName", "displayName", "name") } : {}),
          ...(firstString(sources, "locationId", "location_id") ? { locationId: firstString(sources, "locationId", "location_id") } : {}),
          ...(firstString(sources, "locationName", "location_name", "venueName", "officeName", "displayName", "name") ? { locationName: firstString(sources, "locationName", "location_name", "venueName", "officeName", "displayName", "name") } : {}),
          ...(firstString(sources, "startAt", "startsAt", "start", "requestedStartAt") ? { startAt: firstString(sources, "startAt", "startsAt", "start", "requestedStartAt") } : {}),
          ...(firstString(sources, "endAt", "endsAt", "end", "requestedEndAt") ? { endAt: firstString(sources, "endAt", "endsAt", "end", "requestedEndAt") } : {}),
          ...(firstString(sources, "timezone", "timeZone") ? { timezone: firstString(sources, "timezone", "timeZone") } : {}),
          ...(firstString(sources, "slotKey", "slot_key") ? { slotKey: firstString(sources, "slotKey", "slot_key") } : {}),
          ...(firstNumber(sources, "durationMinutes", "duration_minutes", "duration") !== undefined ? { durationMinutes: firstNumber(sources, "durationMinutes", "duration_minutes", "duration") } : {}),
          ...(firstNumber(sources, "persons", "quantity") !== undefined ? { persons: firstNumber(sources, "persons", "quantity") } : {}),
          ...(firstNumber(sources, "amountCents", "amount_cents") !== undefined ? { amountCents: firstNumber(sources, "amountCents", "amount_cents") } : {}),
          ...(firstNumber(sources, "totalCents", "total_cents") !== undefined ? { totalCents: firstNumber(sources, "totalCents", "total_cents") } : {}),
          ...(firstNumber(sources, "taxCents", "tax_cents") !== undefined ? { taxCents: firstNumber(sources, "taxCents", "tax_cents") } : {}),
          ...(firstString(sources, "currency") ? { currency: firstString(sources, "currency") } : {}),
          ...(proposalOccurrenceStatus(firstString(sources, "status")) ? { status: proposalOccurrenceStatus(firstString(sources, "status")) } : {}),
          ...(occurrenceAdjusted ? { adjusted: true } : {}),
          ...(occurrenceAdjustmentReason ? { adjustmentReason: occurrenceAdjustmentReason } : {}),
          ...(row.adjustmentAccepted === true ? { adjustmentAccepted: true } : {}),
          ...(Array.isArray(row.lines) ? { lines: row.lines } : Array.isArray(occurrencePricing?.lines) ? { lines: occurrencePricing.lines } : {}),
          ...(occurrenceAppliedRules ? { appliedRules: occurrenceAppliedRules } : {})
        };
        return [occurrence];
      });
      return {
        id,
        proposalId: firstString([item, record], "proposalId") ?? id,
        kind: firstString([item, record], "kind", "targetKind"),
        targetKind: firstString([item, record], "targetKind", "kind"),
        currency: firstString([item, record], "currency"),
        totalCents: Math.max(0, Math.round(totalCents)),
        expiresAt,
        ...(lines ? { lines } : {}),
        ...(Object.keys(appointment).length ? { appointment } : {}),
        ...(occurrences?.length ? { occurrences } : {}),
        ...(appliedRules ? { appliedRules } : {}),
        ...(dynamicAdjustmentCents !== undefined ? { dynamicAdjustmentCents } : {}),
        ...(adjusted ? { adjusted: true } : {}),
        ...(adjustmentReason ? { adjustmentReason } : {}),
        ...(item.adjustmentAccepted === true || record.adjustmentAccepted === true ? { adjustmentAccepted: true } : {}),
        ...(record.consentRequired === true || item.consentRequired === true ? { consentRequired: true } : {})
      };
    }
  }
  for (const item of Object.values(record)) {
    const found = quoteFromValue(item, depth + 1);
    if (found) return found;
  }
  return undefined;
}

function secureUrl(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length > 2_048) return undefined;
  try {
    const url = new URL(value);
    const local = url.protocol === "http:" && ["localhost", "127.0.0.1", "::1"].includes(url.hostname);
    if (url.protocol !== "https:" && !local) return undefined;
    if (url.username || url.password || url.hash) return undefined;
    return url.toString();
  } catch { return undefined; }
}

export function checkoutFromValue(value: unknown, depth = 0): CheckoutSnapshot | undefined {
  if (depth > 5 || !value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = checkoutFromValue(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const payload = record.payload && typeof record.payload === "object" && !Array.isArray(record.payload) ? record.payload as Record<string, unknown> : record;
  const nestedCheckout = objectRecord(payload.checkout);
  const checkoutPayload = nestedCheckout ? { ...payload, ...nestedCheckout } : payload;
  const payment = objectRecord(checkoutPayload.payment);
  const paymentLink = objectRecord(checkoutPayload.paymentLink);
  const paymentId = typeof checkoutPayload.paymentId === "string" ? checkoutPayload.paymentId : typeof payment?.paymentId === "string" ? payment.paymentId : typeof payment?.id === "string" ? payment.id : undefined;
  const checkoutUrl = secureUrl(checkoutPayload.checkoutUrl) ?? secureUrl(payment?.checkoutUrl) ?? secureUrl(paymentLink?.url);
  // A bare `{ type: "checkout" }` is not a checkout. Only render the block
  // after Kuroo returned an actionable identity or a hosted URL.
  if (paymentId || checkoutUrl || typeof checkoutPayload.bookingId === "string" || typeof checkoutPayload.eventOrderId === "string") {
    return {
      ...(paymentId ? { paymentId } : {}),
      ...(typeof checkoutPayload.bookingId === "string" ? { bookingId: checkoutPayload.bookingId } : {}),
      ...(typeof checkoutPayload.eventOrderId === "string" ? { eventOrderId: checkoutPayload.eventOrderId } : {}),
      ...(checkoutUrl ? { checkoutUrl } : {}),
      ...(typeof checkoutPayload.status === "string" ? { status: checkoutPayload.status } : typeof payment?.status === "string" ? { status: payment.status } : {}),
      ...(typeof checkoutPayload.statusDetail === "string" ? { statusDetail: checkoutPayload.statusDetail } : {}),
      ...(typeof checkoutPayload.bookingStatus === "string" ? { bookingStatus: checkoutPayload.bookingStatus } : {}),
      ...(typeof checkoutPayload.eventOrderStatus === "string" ? { eventOrderStatus: checkoutPayload.eventOrderStatus } : {}),
      ...(typeof checkoutPayload.recurringSeriesStatus === "string" ? { recurringSeriesStatus: checkoutPayload.recurringSeriesStatus } : {}),
      ...(typeof checkoutPayload.recurringPaymentState === "string" ? { recurringPaymentState: checkoutPayload.recurringPaymentState } : {}),
      ...(typeof checkoutPayload.statusToken === "string" && checkoutPayload.statusToken.length <= 500 ? { statusToken: checkoutPayload.statusToken } : typeof payment?.statusToken === "string" && payment.statusToken.length <= 500 ? { statusToken: payment.statusToken } : {}),
      ...(typeof checkoutPayload.amountCents === "number" ? { amountCents: checkoutPayload.amountCents } : typeof payment?.amountCents === "number" ? { amountCents: payment.amountCents } : {}),
      ...(typeof checkoutPayload.currency === "string" ? { currency: checkoutPayload.currency } : typeof payment?.currency === "string" ? { currency: payment.currency } : {})
    };
  }
  for (const item of Object.values(record)) {
    const found = checkoutFromValue(item, depth + 1);
    if (found) return found;
  }
  return undefined;
}

export function isConciergeCheckoutRenderable(checkout: CheckoutSnapshot | undefined): boolean {
  return Boolean(checkout && (checkout.checkoutUrl || checkout.paymentId || checkout.bookingId || checkout.eventOrderId));
}

export function quoteOccurrencesFromSnapshot(quote: QuoteSnapshot): QuoteOccurrence[] {
  if (quote.occurrences?.length) return quote.occurrences;
  if (!quote.appointment) return [];
  return [{
    sequence: 0,
    quoteId: quote.id,
    ...quote.appointment,
    totalCents: quote.totalCents,
    currency: quote.currency,
    appliedRules: quote.appliedRules,
    adjusted: quote.adjusted,
    adjustmentReason: quote.adjustmentReason,
    ...(quote.adjustmentAccepted === undefined ? {} : { adjustmentAccepted: quote.adjustmentAccepted })
  }];
}

function conciergeCustomerIsValid(customer: ConciergeCustomer): boolean {
  return customer.firstName.trim().length > 0 && /^\S+@\S+\.\S+$/u.test(customer.email.trim());
}

export function isConciergeProposalConfirmable(quote: QuoteSnapshot | undefined, customer: ConciergeCustomer, now = Date.now()): boolean {
  if (!quote || !quote.id || !quote.expiresAt || Number.isNaN(Date.parse(quote.expiresAt)) || Date.parse(quote.expiresAt) <= now) return false;
  if (!conciergeCustomerIsValid(customer)) return false;
  if (quote.consentRequired && customer.consent !== true) return false;
  const occurrences = quoteOccurrencesFromSnapshot(quote);
  if (occurrences.length && !occurrences.some((occurrence) => !["excluded", "cancelled", "unavailable", "expired"].includes(occurrence.status ?? ""))) return false;
  return occurrences.every((occurrence) => {
    if (["excluded", "cancelled"].includes(occurrence.status ?? "")) return true;
    if (["unavailable", "expired"].includes(occurrence.status ?? "")) return false;
    if (!occurrence.quoteId) return false;
    if ((quote.kind ?? quote.targetKind) === "service" && !occurrence.holdId) return false;
    return true;
  });
}

export function eventFromValue(value: unknown, depth = 0): { kind: "quote" | "hold" | "checkout"; value: unknown } | undefined {
  if (depth > 5 || !value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = eventFromValue(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (quoteFromValue(record)) return { kind: "quote", value: quoteFromValue(record) };
  if (typeof record.holdId === "string" && typeof record.expiresAt === "string") return { kind: "hold", value: record };
  if (typeof record.paymentId === "string" || (record.payment && typeof record.payment === "object") || (record.paymentLink && typeof record.paymentLink === "object")) return { kind: "checkout", value: record };
  for (const item of Object.values(record)) {
    const found = eventFromValue(item, depth + 1);
    if (found) return found;
  }
  return undefined;
}

